/**
 * components/profile/LifePrioritiesTab.jsx — v3.7.7 Phase 84.31
 *
 * Thin wrapper around the existing `LifeProfileEmbed`. The real work
 * (fact-lifecycle model, "What 2120 understands" summary, "How this
 * affects your plan" panel, single-primary / optional-secondary priority
 * model, timestamped emotional check-ins) is Phase 2 of the redesign
 * and will replace this shim without changing the tab shell.
 *
 * Keeping the wrapper minimal on purpose:
 *   • Preserves every existing user answer.
 *   • Preserves every existing catalog question.
 *   • Preserves autosave and the tier badge that longtime users rely on.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';
import { LifeProfileEmbed } from '@/pages/SettingsLifeProfile';
import ProfileUnderstandsCard from '@/components/profile/ProfileUnderstandsCard';
import PlanImpactCard from '@/components/profile/PlanImpactCard';
import PriorityMigrationWizard from '@/components/profile/PriorityMigrationWizard';
import ProfileReadinessCard from '@/components/profile/ProfileReadinessCard';
import CheckInPromptCard from '@/components/profile/CheckInPromptCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LifePrioritiesTab() {
  // v3.7.8 Phase 84.31 Part 2 — Single fetch shared by both hero cards.
  // Each card previously fired its own request against
  // /api/profile/summary which serialised behind a slow snapshot build.
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await axios.get(`${BACKEND_URL}/api/profile/summary`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        timeout: 20000,
      });
      setSummary(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await axios.get(`${BACKEND_URL}/api/profile/summary`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
          timeout: 20000,
        });
        if (!cancelled) setSummary(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-5" data-testid="profile-life-tab">
      {/* Legacy "pick up to three" → primary/secondary wizard.
          Renders null when not needed; safe to keep mounted. */}
      <PriorityMigrationWizard
        summary={summary}
        onCompleted={(res) => { if (!res?.skipped) load(); }}
      />
      <CheckInPromptCard summary={summary} />
      <ProfileReadinessCard summary={summary} loading={loading} />
      <ProfileUnderstandsCard summary={summary} loading={loading} error={error} />
      <PlanImpactCard         summary={summary} loading={loading} error={error} />
      <LifeProfileEmbed showTitle={false} />
    </div>
  );
}
